import qs from 'qs'
import { cacheAdapterEnhancer } from 'axios-extensions'
import LRUCache from 'lru-cache'
import { v4 as uuidv4 } from 'uuid'
import * as Sentry from '@sentry/browser'

const cacheConfig = new LRUCache({
  // 有效期2mins
  ttl: 1000 * 60 * 2,
  // 最大缓存数量
  max: 1000
})

// 全局變數，用於追蹤 token 刷新過程
let isRefreshing = false
// 存儲等待 token 刷新完成的請求隊列
let refreshSubscribers = []

// 執行隊列中等待的請求
const processQueue = (error = null) => {
  refreshSubscribers.forEach(callback => {
    if (error) {
      callback(error)
    } else {
      callback()
    }
  })
  // 處理完後清空隊列
  refreshSubscribers = []
}

export default function ({ $axios, store, app, route }, inject) {
  const { CancelToken } = $axios
  const onRequest = config => {
    config.cancelToken = new CancelToken((cancel) => {
      store.dispatch('axiosPending/pushAxiosPending', JSON.stringify({ cancel, config }))
    })
    const xCorrelationId = uuidv4()
    config.headers['X-Correlation-ID'] = xCorrelationId
    Sentry.setTag('X-Correlation-ID', xCorrelationId)

    // SSR 時將客戶端真實 IP 轉發給後端（僅對 asyncData/fetch 等 SSR 請求有效）
    // 瀏覽器直接發的請求（mounted/methods）不會經過這裡的 process.server 判斷
    if (process.server && app.context.req) {
      const req = app.context.req
      const forwardedFor = req.headers['x-forwarded-for']
      const realIp = req.headers['x-real-ip']
      if (forwardedFor) config.headers['X-Forwarded-For'] = forwardedFor
      if (realIp) config.headers['X-Real-IP'] = realIp
    }

    return config
  }
  const onResponse = response => {
    // 移除 pending
    store.dispatch('axiosPending/removeAxiosPending', response.config)
    return response
  }
  const onError = async error => {
    console.log('error', error)
    console.log('route', route)
    if (process.server) {
      return Promise.reject(error)
    }
    const originalRequest = error.config
    if (originalRequest) {
      store.dispatch('axiosPending/removeAxiosPending', originalRequest)
    }
    // 如果是刷新 Token 的請求，直接拒絕，避免進入重試邏輯造成死鎖
    if (originalRequest && originalRequest.url.includes('/api/Authentication/ECToken/Refresh')) {
      return Promise.reject(error)
    }
    if (error.response && error.response.status === 401) {
      // 如果沒有 refresh token 或是 已經登出(無 access token)，則清除快取並登出
      const currentToken = app.$auth.getToken('local')
      const currentRefreshToken = app.$auth.$storage.getUniversal('refresh_token')

      if (!currentToken || !currentRefreshToken) {
        // 登出
        await store.dispatch('logOut')
        return Promise.reject(error)
      }

      // 防止重複嘗試刷新 token 導致的無限循環
      if (originalRequest._retry) {
        return Promise.reject(error)
      }

      originalRequest._retry = true

      // 判斷請求來源，選擇對應的 axios instance
      let axiosInstance = $axios
      // 根據 baseURL 判斷來源
      if (originalRequest.baseURL === process.env.API_STATIC_ECS_BASE_URL) {
        axiosInstance = app.$staticEcsApi
      } else if (originalRequest.baseURL === process.env.API_SERVICE_ACCOUNT_SET_BASE_URL) {
        axiosInstance = app.$serviceAccountSetApi
      }

      // 如果已經在刷新 token，則加入等待隊列
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          // 將當前請求加入隊列，等待token刷新後再重試
          refreshSubscribers.push(err => {
            if (err) {
              return reject(err)
            }
            // 更新當前請求的 token
            const token = app.$auth.getToken('local')
            originalRequest.headers.Authorization = token
            // 重新發送原始請求，使用對應的 instance
            resolve(axiosInstance.request(originalRequest))
          })
        })
      }

      // 標記正在刷新 token
      isRefreshing = true

      try {
        // 手動 refresh token，並寫入 $auth
        const res = await app.$axios.post('/api/Authentication/ECToken/Refresh', {
          AccessToken: app.$auth.getToken('local'),
          RefreshToken: app.$auth.$storage.getUniversal('refresh_token')
        })

        // 將新的 token 設定到 $auth
        const { Data } = res.data
        await app.$auth.setUserToken(Data)

        // 刷新成功，更新狀態
        isRefreshing = false

        // 更新 axios header
        const token = app.$auth.getToken('local')
        // 同時更新所有使用$axios.create創建的實例
        if (app.$staticEcsApi) app.$staticEcsApi.setHeader('Authorization', token)
        if (app.$serviceAccountSetApi) app.$serviceAccountSetApi.setHeader('Authorization', token)

        // 處理隊列中等待的請求
        processQueue()

        // 更新原始請求的 token
        originalRequest.headers.Authorization = token

        // 重新發送原始請求，使用對應的 instance
        return axiosInstance.request(originalRequest)
      } catch (refreshError) {
        // 刷新失敗，登出
        isRefreshing = false
        processQueue(refreshError)
        console.error('刷新 token 失敗：', refreshError)
        await store.dispatch('logOut')
        return Promise.reject(refreshError)
      }
    }
    return Promise.reject(error)
  }

  // Authorization
  $axios.setHeader('Content-Type', 'application/json')
  $axios.setHeader('Cache-Control', 'no-store')

  // 設定全域 timeout (30 秒)
  $axios.defaults.timeout = 30000

  $axios.defaults.paramsSerializer = params => {
    return qs.stringify(params, { arrayFormat: 'repeat' })
  }
  const defaultAdapter = $axios.defaults.adapter
  $axios.defaults.adapter = cacheAdapterEnhancer(defaultAdapter, {
    enabledByDefault: false,
    cacheFlag: 'useCache',
    defaultCache: cacheConfig
  })

  $axios.setBaseURL(process.env.API_ECS_BASE_URL)
  $axios.onRequest(onRequest) // API攔截器（送出前）
  $axios.onResponse(onResponse) // API攔截器（送出後）
  $axios.onError(onError) // API攔截器（送出後）

  // PlusPay 用 domain API
  const staticEcsApi = $axios.create({
    headers: { 'Content-Type': 'application/json' },
    timeout: 30000
  })
  staticEcsApi.setBaseURL(process.env.API_STATIC_ECS_BASE_URL)
  staticEcsApi.onRequest(config => {
    if (app.$auth.loggedIn && !config.headers.common.Authorization) {
      // 若已登入但未設定到 token(Authorization)
      staticEcsApi.setHeader('Authorization', app.$auth.getToken('local'))
      config.headers.common.Authorization = app.$auth.getToken('local')
    }
    onRequest(config)
  }) // API攔截器（送出前）
  staticEcsApi.onResponse(onResponse) // API攔截器（送出後）
  staticEcsApi.onError(onError) // API攔截器（送出後） - 添加錯誤處理器
  inject('staticEcsApi', staticEcsApi)

  // Nuxt Node.js 用 domain API
  const serviceApi = $axios.create({
    headers: { 'Content-Type': 'application/json' },
    timeout: 30000
  })
  serviceApi.setBaseURL('') // 不需 baseUrl
  serviceApi.onRequest(config => {
    onRequest(config)
  }) // API攔截器（送出前）
  serviceApi.onResponse(onResponse) // API攔截器（送出後）
  inject('serviceApi', serviceApi)

  // 會員整合 用 domain API
  const serviceAccountSetApi = $axios.create({
    headers: { 'Content-Type': 'application/json' },
    timeout: 30000
  })
  serviceAccountSetApi.setBaseURL(process.env.API_SERVICE_ACCOUNT_SET_BASE_URL)
  serviceAccountSetApi.onRequest(config => {
    if (app.$auth.loggedIn && !config.headers.common.Authorization) {
      // 若已登入但未設定到 token(Authorization)
      serviceAccountSetApi.setHeader('Authorization', app.$auth.getToken('local'))
      config.headers.common.Authorization = app.$auth.getToken('local')
    }
    onRequest(config)
  }) // API攔截器（送出前）
  serviceAccountSetApi.onResponse(onResponse) // API攔截器（送出後）
  serviceAccountSetApi.onError(onError) // API攔截器（送出後） - 添加錯誤處理器
  inject('serviceAccountSetApi', serviceAccountSetApi)
}
